/**
 * Authentication flow session management utilities
 *
 * Provides a centralized way to manage authentication flow state
 * in sessionStorage, ensuring consistency across the application.
 */

// ---------------------------------------------------------------------------
// Onboarding: persist profile data through OAuth redirects
// ---------------------------------------------------------------------------
// When a user starts the onboarding flow and then signs up via Google/Microsoft,
// the full-page OAuth redirect destroys React state. We serialise the
// WorkingProfileForm and previewToken to sessionStorage before the redirect and
// restore them in AuthenticatedRootElement once the user lands back in the app.

const ONBOARDING_PROFILE_KEY = 'onboarding_pending_profile';

export interface OnboardingSessionData {
  profileFormData: object;
  previewToken: string | null;
}

export const OnboardingSession = {
  /** Save the profile form data (and optional previewToken) before an OAuth redirect. */
  save: (data: OnboardingSessionData): void => {
    try {
      sessionStorage.setItem(ONBOARDING_PROFILE_KEY, JSON.stringify(data));
    } catch {
      // sessionStorage unavailable — silently skip; the user will lose the pre-fill
    }
  },

  /** Load the saved session data after returning from an OAuth redirect. */
  load: <T extends OnboardingSessionData = OnboardingSessionData>(): T | null => {
    try {
      const raw = sessionStorage.getItem(ONBOARDING_PROFILE_KEY);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },

  /** Returns true if there is pending onboarding session data waiting. */
  hasPending: (): boolean => {
    try {
      return sessionStorage.getItem(ONBOARDING_PROFILE_KEY) !== null;
    } catch {
      return false;
    }
  },

  /** Remove the saved data (call after successfully claiming the profile). */
  clear: (): void => {
    try {
      sessionStorage.removeItem(ONBOARDING_PROFILE_KEY);
    } catch {
      // no-op
    }
  },
};

export const AUTH_FLOW_TYPES = {
  SHOPIFY: 'shopify',
} as const;

export type AuthFlowType =
  (typeof AUTH_FLOW_TYPES)[keyof typeof AUTH_FLOW_TYPES];

/**
 * Utility class for managing authentication flow session state
 */
export const AuthFlowSession = {
  /**
   * Set the authentication flow to Shopify
   * @throws {Error} If sessionStorage is not available
   */
  setShopify: (): void => {
    try {
      sessionStorage.setItem('auth_flow', AUTH_FLOW_TYPES.SHOPIFY);
    } catch (error) {
      console.error(
        'Failed to set Shopify auth flow in sessionStorage:',
        error,
      );
      throw new Error('Session storage unavailable');
    }
  },

  /**
   * Check if the current authentication flow is Shopify
   * @returns {boolean} true if the current flow is Shopify
   */
  isShopify: (): boolean => {
    try {
      return sessionStorage.getItem('auth_flow') === AUTH_FLOW_TYPES.SHOPIFY;
    } catch (error) {
      console.warn('Failed to read auth flow from sessionStorage:', error);
      return false;
    }
  },

  /**
   * Get the current authentication flow type
   * @returns {AuthFlowType | null} the current flow type or null if not set
   */
  getFlowType: (): AuthFlowType | null => {
    try {
      const flowType = sessionStorage.getItem('auth_flow');
      return Object.values(AUTH_FLOW_TYPES).includes(flowType as AuthFlowType)
        ? (flowType as AuthFlowType)
        : null;
    } catch (error) {
      console.warn('Failed to get auth flow type from sessionStorage:', error);
      return null;
    }
  },

  /**
   * Clear the authentication flow from session storage
   */
  clear: (): void => {
    try {
      sessionStorage.removeItem('auth_flow');
    } catch (error) {
      console.warn('Failed to clear auth flow from sessionStorage:', error);
      // Don't throw here as clearing is often used in cleanup scenarios
    }
  },

  /**
   * Set a custom authentication flow type
   * @param {AuthFlowType} flowType - The flow type to set
   * @throws {Error} If sessionStorage is not available
   */
  setFlowType: (flowType: AuthFlowType): void => {
    try {
      sessionStorage.setItem('auth_flow', flowType);
    } catch (error) {
      console.error('Failed to set auth flow type in sessionStorage:', error);
      throw new Error('Session storage unavailable');
    }
  },

  /**
   * Check if sessionStorage is available
   * @returns {boolean} true if sessionStorage is available and working
   */
  isAvailable: (): boolean => {
    try {
      const testKey = '__auth_flow_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  },
};
